import { CircleX } from "lucide-react";
import type { FC } from "react";

import { CustomBadge } from "@/components/atoms";
import { cn } from "@/lib/utils";
import { formatTestId } from "@/utils/formatTestId";

import type { StatusVariants } from "../types";
import FilterWrapper from "./FilterWrapper";

interface SelectedCategoryBadgeProps {
  category: string;
  selected: string[];
  onClear: (category: string) => void;
  onOpen: (category: string) => void;
  statusVariantMap?: StatusVariants;
  classes?: string;
  isBadge?: boolean;
}

const SelectedCategoryBadge: FC<SelectedCategoryBadgeProps> = ({
  category,
  selected,
  onClear,
  onOpen,
  statusVariantMap,
  classes,
  isBadge = true,
}) => {
  const categoryId = formatTestId(category);

  return (
    <FilterWrapper classes={cn(classes)}>
      <>
        <button
          onClick={() => onClear(category)}
          className="cursor-pointer"
          data-testid={`selected-category-clear-${categoryId}`}
        >
          <CircleX size={16} className="size-[16px] mr-2" />
        </button>

        <button
          className="flex items-center gap-[5px] cursor-pointer"
          onClick={() => onOpen(category)}
          data-testid={`selected-category-open-${categoryId}`}
        >
          <span className="leading-[18px]">{category}</span>

          {selected.length > 2 ? (
            <CustomBadge
              size="xs"
              className="px-[5px] font-[Geist] text-[10px]"
              data-testid={`selected-category-count-${categoryId}`}
            >
              {selected.length} Selected
            </CustomBadge>
          ) : (
            selected.map(item => {
              const variant = statusVariantMap?.[item]?.badge ?? "default";
              const itemId = formatTestId(item);

              return (
                <>
                  {isBadge ? (
                    <CustomBadge
                      key={item}
                      variant={variant}
                      size="xs"
                      className="px-[5px] ml-2 font-[Geist] text-[10px]"
                      data-testid={`selected-category-item-${categoryId}-${itemId}`}
                    >
                      {item}
                    </CustomBadge>
                  ) : (
                    <p className="font-[Geist] text-[10px]">| {item}</p>
                  )}
                </>
              );
            })
          )}
        </button>
      </>
    </FilterWrapper>
  );
};

export default SelectedCategoryBadge;
